import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnabled = Boolean(import.meta.env.PROD && dsn);
const sentryEnvironment = import.meta.env.MODE || 'production';

let initialized = false;

const SAFARI_NETWORK_ERROR_MESSAGES = new Set([
  'Load failed',
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
]);

const NON_SAFARI_WEBKIT_TOKENS = /(Chrome|Chromium|CriOS|FxiOS|Edg|OPR|DuckDuckGo|YaBrowser)/i;

const getUserAgent = (event) => {
  const headers = event?.request?.headers;
  const eventUserAgent = headers?.['User-Agent'] ?? headers?.['user-agent'];

  if (eventUserAgent) {
    return eventUserAgent;
  }

  return globalThis.navigator?.userAgent;
};

const isSafariOrWebViewUserAgent = (userAgent) => {
  if (!userAgent || NON_SAFARI_WEBKIT_TOKENS.test(userAgent)) {
    return false;
  }

  const isAppleWebKit = userAgent.includes('AppleWebKit');
  const isApplePlatform = /\b(Macintosh|iPhone|iPad|iPod)\b/.test(userAgent);
  const isSafari = userAgent.includes('Safari') && userAgent.includes('Version/');
  const isAppleMobileWebView = /\b(iPhone|iPad|iPod)\b/.test(userAgent) && userAgent.includes('Mobile');

  return isAppleWebKit && isApplePlatform && (isSafari || isAppleMobileWebView);
};

const isSafariNetworkFetchFailure = (event, hint) => {
  const originalException = hint?.originalException;
  const exception = event?.exception?.values?.[0];
  const message = exception?.value ?? originalException?.message ?? event?.message;
  const errorType = exception?.type ?? originalException?.name;

  if (
    errorType !== 'TypeError' ||
    !SAFARI_NETWORK_ERROR_MESSAGES.has(message) ||
    !isSafariOrWebViewUserAgent(getUserAgent(event))
  ) {
    return false;
  }

  const hasStacktrace = Boolean(exception?.stacktrace?.frames?.length);
  if (hasStacktrace) {
    return false;
  }

  return event?.breadcrumbs?.some(
    (breadcrumb) =>
      breadcrumb?.category === 'fetch' &&
      breadcrumb?.level === 'error' &&
      breadcrumb?.data?.url?.includes('/api/graphql')
  );
};

const fallbackLogger = {
  trace: (...args) => console.debug(...args),
  debug: (...args) => console.debug(...args),
  info: (...args) => console.info(...args),
  warn: (...args) => console.warn(...args),
  error: (...args) => console.error(...args),
  fatal: (...args) => console.error(...args),
  fmt: (strings, ...values) =>
    strings.reduce(
      (acc, current, index) =>
        `${acc}${current}${index < values.length ? String(values[index]) : ''}`,
      ''
    ),
};

export const logger = fallbackLogger;

export const initSentry = () => {
  if (initialized || !sentryEnabled) {
    return;
  }

  Sentry.init({
    dsn,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.commit2act\.org/,
    ],
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    environment: sentryEnvironment,
    beforeSend(event, hint) {
      if (isSafariNetworkFetchFailure(event, hint)) {
        return null;
      }

      return event;
    },
  });

  initialized = true;
};

export const captureException = (error, context) => {
  if (!sentryEnabled) {
    return undefined;
  }

  return Sentry.captureException(error, context);
};

const noopSpan = {
  setAttribute: () => {},
  setAttributes: () => {},
  end: () => {},
};

export const startSpan = (options, callback) => {
  if (!sentryEnabled) {
    return callback(noopSpan);
  }

  return Sentry.startSpan(options, callback);
};
